import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Save,
  Edit,
  Cancel,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente Mi Perfil - Para todos los usuarios
 * Path: backoffice-web/src/pages/Perfil/Perfil.js
 * Última modificación: 2025-12-31 01:00
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Perfil() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });

  const roles = {
    1: { nombre: 'Administrador', color: 'error' },
    2: { nombre: 'Conductor', color: 'primary' },
    3: { nombre: 'Rendidor', color: 'warning' },
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/me');
      setUser(response.data.user || response.data);
      setFormData({
        nombre: response.data.user?.nombre || response.data.nombre || '',
        email: response.data.user?.email || response.data.email || '',
      });
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
      setError('Error al cargar los datos del perfil');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.put(`/users/${user.id}`, formData);
      setSuccess('Perfil actualizado exitosamente');
      setEditMode(false);
      await fetchUserData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        setError(`Error de validación: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Error al actualizar el perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validaciones frontend
    if (!passwordData.current_password) {
      setError('Debe ingresar su contraseña actual');
      setLoading(false);
      return;
    }

    if (!passwordData.password) {
      setError('Debe ingresar una nueva contraseña');
      setLoading(false);
      return;
    }

    if (passwordData.password.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    if (passwordData.password !== passwordData.password_confirmation) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      await api.put(`/users/${user.id}`, {
        password: passwordData.password,
        current_password: passwordData.current_password,
      });
      
      setSuccess('Contraseña actualizada exitosamente');
      setPasswordData({
        current_password: '',
        password: '',
        password_confirmation: '',
      });
      setShowPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        setError(`Error de validación: ${errorMessages}`);
      } else if (error.response?.status === 401) {
        setError('La contraseña actual es incorrecta');
      } else {
        setError(error.response?.data?.message || 'Error al cambiar la contraseña');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData({
      nombre: user.nombre || '',
      email: user.email || '',
    });
    setError('');
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  const roleInfo = roles[user.role_id] || { nombre: 'Usuario', color: 'default' };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Mi Perfil
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Información del Usuario */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto 16px',
                bgcolor: roleInfo.color + '.main',
                fontSize: '3rem',
              }}
            >
              {user.nombre?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Typography variant="h5" gutterBottom>
              {user.nombre}
            </Typography>
            <Chip
              label={roleInfo.nombre}
              color={roleInfo.color}
              sx={{ mb: 2 }}
            />
            <Divider sx={{ my: 2 }} />
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <Email sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Person sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                ID: {user.id}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Editar Datos Personales */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Datos Personales
              </Typography>
              {!editMode && (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setEditMode(true)}
                >
                  Editar
                </Button>
              )}
            </Box>

            <Box component="form" onSubmit={handleUpdateProfile}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    disabled={!editMode}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!editMode}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {editMode && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancelEdit}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Cambiar Contraseña */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Cambiar Contraseña
            </Typography>

            <Box component="form" onSubmit={handleChangePassword}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contraseña Actual *"
                    name="current_password"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nueva Contraseña *"
                    name="password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.password}
                    onChange={handlePasswordChange}
                    helperText="Mínimo 8 caracteres"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirmar Nueva Contraseña *"
                    name="password_confirmation"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.password_confirmation}
                    onChange={handlePasswordChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Lock />}
                  disabled={loading}
                >
                  {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
